import {
  ControlledDateInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import {
  Checkbox,
  ControlledTextArea,
  MaskedInput,
  TextInput,
} from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import SectionBreak from "components/SectionBreak";
import React from "react";
import type {
  Control,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import type { ConstantTypes } from "types/DataTypes";

import type { LienFormValues } from "../../../LienTypes";
import EventNumbersSection from "./EventNumbersSection";

type InitialLienRequestSectionProps = {
  control: Control<LienFormValues>;
  getValues: UseFormGetValues<LienFormValues>;
  setValue: UseFormSetValue<LienFormValues>;
  handleFieldChange: (field: string | null, value: unknown) => Promise<void>;
  constants?: ConstantTypes;
};

const InitialLienRequestSection = ({
  control,
  getValues,
  setValue,
  handleFieldChange,
  constants,
}: InitialLienRequestSectionProps) => {
  const submittedOn = getValues("initial_lien_request_submitted_on");

  return (
    <>
      <h3>Initial Lien Request</h3>
      <ControlledDateInput
        control={control}
        name="initial_lien_request_submitted_on"
        label="Date Request Submitted"
        onSave={async (value) => {
          if (!value) {
            setValue("initial_lien_request_tracking_number", null);
            setValue("initial_lien_request_plan_name", null);
            setValue("initial_lien_request_plan_address", null);
            setValue("initial_lien_request_plan_contact_number", null);
            setValue("initial_lien_request_subrogation_agent_name", null);
            setValue("initial_lien_request_subrogation_phone", null);
            setValue("initial_lien_request_ack_received", false);
            setValue("initial_lien_request_analyst_name", null);
            setValue("initial_lien_request_analyst_phone", null);
            setValue("initial_lien_request_analyst_email", null);
            setValue("initial_lien_request_no_interest_letter_received", false);
            setValue("initial_lien_request_itemization_received", false);
            setValue("initial_lien_request_event_numbers", []);
            setValue("initial_lien_request_method", null);
            setValue("cpn_amount", null);
            setValue("cpn_total_disputed", null);
            setValue("cpn_dispute_date", null);
            setValue("part_c_d_result", null);
            setValue("cpn_savings", null);
            await handleFieldChange(null, {
              initial_lien_request_submitted_on: null,
              initial_lien_request_tracking_number: null,
              initial_lien_request_plan_name: null,
              initial_lien_request_plan_address: null,
              initial_lien_request_plan_contact_number: null,
              initial_lien_request_subrogation_agent_name: null,
              initial_lien_request_subrogation_phone: null,
              initial_lien_request_ack_received: false,
              initial_lien_request_analyst_name: null,
              initial_lien_request_analyst_phone: null,
              initial_lien_request_analyst_email: null,
              initial_lien_request_no_interest_letter_received: false,
              initial_lien_request_itemization_received: false,
              initial_lien_request_event_numbers: [],
              initial_lien_request_method: null,
              cpn_amount: null,
              cpn_total_disputed: null,
              cpn_dispute_date: null,
              part_c_d_result: null,
              cpn_savings: null,
            });
          } else {
            await handleFieldChange("initial_lien_request_submitted_on", value);
          }
        }}
        errors={{}}
      />
      {submittedOn && (
        <>
          <ControlledInput
            control={control}
            name="initial_lien_request_tracking_number"
            label="Request Tracking Number "
            onSave={(value) =>
              handleFieldChange("initial_lien_request_tracking_number", value)
            }
            errors={{}}
            componentType={TextInput}
          />
          <SectionBreak />
          <EventNumbersSection
            control={control}
            getValues={getValues}
            setValue={setValue}
            handleFieldChange={handleFieldChange}
          />
          <SectionBreak />
          <ControlledInput
            control={control}
            name="initial_lien_request_plan_name"
            label="Plan Name"
            onSave={(value) =>
              handleFieldChange("initial_lien_request_plan_name", value)
            }
            errors={{}}
            componentType={TextInput}
          />
          <ControlledInput
            control={control}
            name="initial_lien_request_plan_address"
            label="Plan Address"
            onSave={(value) =>
              handleFieldChange("initial_lien_request_plan_address", value)
            }
            errors={{}}
            componentType={TextInput}
          />
          <ControlledInput
            control={control}
            name="initial_lien_request_plan_contact_number"
            label="Contract No."
            onSave={(value) =>
              handleFieldChange(
                "initial_lien_request_plan_contact_number",
                value,
              )
            }
            errors={{}}
            componentType={MaskedInput}
            mask="phone"
          />
          <ControlledInput
            control={control}
            name="initial_lien_request_subrogation_agent_name"
            label="Subrogation Agent Name"
            onSave={(value) =>
              handleFieldChange(
                "initial_lien_request_subrogation_agent_name",
                value,
              )
            }
            errors={{}}
            componentType={TextInput}
          />
          <ControlledInput
            control={control}
            name="initial_lien_request_subrogation_phone"
            label="Subrogation Phone"
            onSave={(value) =>
              handleFieldChange("initial_lien_request_subrogation_phone", value)
            }
            errors={{}}
            componentType={MaskedInput}
            mask="phone"
          />
          <Checkbox
            name="initial_lien_request_ack_received"
            label="Acknowledgment Received"
            checked={control._formValues.initial_lien_request_ack_received}
            onChange={async (e) => {
              setValue("initial_lien_request_ack_received", e.target.checked);
              await handleFieldChange(
                "initial_lien_request_ack_received",
                e.target.checked,
              );
            }}
          />
          <ControlledInput
            control={control}
            name="initial_lien_request_analyst_name"
            label="Analyst Name"
            onSave={(value) =>
              handleFieldChange("initial_lien_request_analyst_name", value)
            }
            errors={{}}
            componentType={TextInput}
          />
          <ControlledInput
            control={control}
            name="initial_lien_request_analyst_phone"
            label="Analyst Phone"
            onSave={(value) =>
              handleFieldChange("initial_lien_request_analyst_phone", value)
            }
            errors={{}}
            componentType={MaskedInput}
            mask="phone"
          />
          <ControlledInput
            control={control}
            name="initial_lien_request_analyst_email"
            label="Analyst Email"
            type="email"
            onSave={(value) =>
              handleFieldChange("initial_lien_request_analyst_email", value)
            }
            errors={{}}
            componentType={TextInput}
          />
          <Checkbox
            name="initial_lien_request_no_interest_letter_received"
            label="No Interest Letter Received"
            checked={
              control._formValues
                .initial_lien_request_no_interest_letter_received
            }
            onChange={async (e) => {
              setValue(
                "initial_lien_request_no_interest_letter_received",
                e.target.checked,
              );
              await handleFieldChange(
                "initial_lien_request_no_interest_letter_received",
                e.target.checked,
              );
            }}
          />
          <Checkbox
            name="initial_lien_request_itemization_received"
            label="Itemization Received"
            checked={
              control._formValues.initial_lien_request_itemization_received
            }
            onChange={async (e) => {
              setValue(
                "initial_lien_request_itemization_received",
                e.target.checked,
              );
              await handleFieldChange(
                "initial_lien_request_itemization_received",
                e.target.checked,
              );
            }}
          />
          <ControlledInput
            control={control}
            name="cpn_amount"
            label="Itemization Total"
            onSave={(value) => handleFieldChange("cpn_amount", value)}
            errors={{}}
            componentType={MaskedInput}
            mask="dollar"
          />
          <ControlledDateInput
            control={control}
            name="cpn_dispute_date"
            label="Dispute Date"
            onSave={(value) => handleFieldChange("cpn_dispute_date", value)}
            errors={{}}
          />
          <ControlledSelect
            control={control}
            name="part_c_d_result"
            label="Result"
            onSave={async (option) => {
              const value = option?.value || null;
              await handleFieldChange("part_c_d_result", value);
            }}
            errors={{}}
            options={constants?.PART_C_D_RESULT_TYPE || []}
          />
          <ControlledInput
            control={control}
            name="cpn_savings"
            label="Savings"
            onSave={(value) => handleFieldChange("cpn_savings", value)}
            errors={{}}
            componentType={MaskedInput}
            mask="dollar"
          />
          <ControlledInput
            control={control}
            name="initial_lien_request_method"
            label="Request Method"
            onSave={(value) =>
              handleFieldChange("initial_lien_request_method", value)
            }
            errors={{}}
            componentType={TextInput}
          />
          <div>
            <label>Notes:</label>
            <ControlledTextArea
              control={control}
              name="initial_lien_request_user_notes"
              label={null}
              advancedEdit={true}
              maxLength={2000}
              errors={{}}
              onSave={(value) =>
                handleFieldChange("initial_lien_request_user_notes", value)
              }
            />
          </div>
          <ControlledInput
            control={control}
            name="cpn_total_disputed"
            label="Total Disputed"
            onSave={async () => Promise.resolve()}
            errors={{}}
            componentType={MaskedInput}
            mask="dollar"
            readOnly
          />
        </>
      )}
    </>
  );
};

export default InitialLienRequestSection;
